// @ts-nocheck
const COLORS = [
  [255, 0, 120],
  [0, 220, 255],
  [255, 200, 0],
  [180, 0, 255],
  [0, 255, 140],
  [255, 80, 0],
  [255, 255, 255],
];

function lerp(a, b, t) { return a + (b - a) * t; }

function beginCircle(ctx, x, y, radius) {
  const safeRadius = Number.isFinite(radius) ? Math.max(0, radius) : 0;
  if (safeRadius <= 0) return false;
  ctx.beginPath();
  ctx.arc(x, y, safeRadius, 0, Math.PI * 2);
  return true;
}

function hslToRgb(h, s, l) {
  s /= 100; l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

function seeded(seed) {
  let s = seed || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

export type CanvasVisualizerKind = 'plasma' | 'vortex' | 'strobe' | 'flash' | 'journey' | 'journey2';

export function makeCanvasVisualizerState(kind: CanvasVisualizerKind, seed = 1) {
  const rand = seeded(seed);
  if (kind === 'plasma') {
    return {
      glowPulse: 0,
      ripples: [],
      orbits: Array.from({ length: 6 }, (_, i) => ({
        angle: (i / 6) * Math.PI * 2,
        speed: 0.008 + i * 0.003,
        dist: 0.18 + i * 0.04,
      })),
    };
  }
  if (kind === 'vortex') {
    return {
      rings: [],
      stars: Array.from({ length: 120 }, () => ({
        angle: rand() * Math.PI * 2,
        dist: rand() * 0.5,
        speed: 0.002 + rand() * 0.004,
        size: 1 + rand() * 2,
      })),
      zoom: 0,
      twist: 0,
      rand,
    };
  }
  if (kind === 'strobe') {
    return {
      flash: 0,
      beamAngle: 0,
      ripples: [],
      particles: [],
      bars: new Float32Array(64).fill(0),
      rand,
    };
  }
  if (kind === 'flash') return { flash: 0, color: [255, 255, 255], rand };
  return {
    frameCount: 0,
    scrollX: 0,
    birdFlap: 0,
    looping: false,
    loopAngle: 0,
    loopDir: 1,
    trail: [],
    beatFlash: 0,
    winPulse: 0,
    wavePulse: 0,
    beatRings: [],
    bldgs: Array.from({ length: kind === 'journey' ? 72 : 55 }, () => ({
      nx: rand(),
      w: 28 + rand() * 60,
      h: 0.12 + rand() * 0.5,
      mast: rand() < 0.35,
      wseed: rand() * 999,
      side: rand() < 0.5 ? -1 : 1,
      wx: (rand() < 0.5 ? -1 : 1) * (13 + rand() * 13),
      wz: 4 + rand() * 200,
      ww: 3 + rand() * 7,
      wh: 6 + rand() * 44,
    })),
    stars: Array.from({ length: 90 }, () => ({
      nx: rand(),
      ny: rand() * 0.5,
      sx: rand(),
      sy: rand() * 0.48,
      r: 0.5 + rand() * 1.2,
    })),
    clouds: Array.from({ length: 22 }, () => ({
      nx: rand(),
      ny: 0.04 + rand() * 0.28,
      w: 65 + rand() * 160,
      h: 18 + rand() * 48,
      lyr: Math.round(rand() * 2),
    })),
    waves: Array.from({ length: 5 }, (_, i) => ({
      phase: rand() * Math.PI * 2,
      spd: 0.018 + i * 0.007,
      amp: 7 + i * 5,
      ny: 0.62 + i * 0.04,
    })),
    rand,
  };
}

export function drawCanvasVisualizer(kind, ctx, W, H, dataArray, shared, st) {
  if (kind === 'plasma') return drawPlasma(ctx, W, H, dataArray, shared, st);
  if (kind === 'vortex') return drawVortex(ctx, W, H, dataArray, shared, st);
  if (kind === 'strobe') return drawStrobe(ctx, W, H, dataArray, shared, st);
  if (kind === 'flash') return drawFlash(ctx, W, H, dataArray, shared, st);
  return drawJourney(ctx, W, H, shared, st, kind === 'journey');
}

function drawPlasma(ctx, W, H, dataArray, shared, st) {
  const { visualEnergy: energy, visualBass: bass, visualMid: mid, hue, isBeat, beatStrength, phase } = shared;
  const cx = W / 2, cy = H / 2;
  if (isBeat) {
    st.glowPulse = Math.min(1, 0.7 + beatStrength * 0.15);
    st.ripples.push({ r: 0, life: 1, hue });
    if (st.ripples.length > 10) st.ripples = st.ripples.slice(-10);
  }
  st.glowPulse = Math.max(0, st.glowPulse - 0.035);
  ctx.fillStyle = `rgba(0,0,3,${0.38 + st.glowPulse * 0.18})`;
  ctx.fillRect(0, 0, W, H);

  const bandCount = 5;
  const bandH = H * 0.65;
  const startY = cy - bandH / 2;
  for (let b = 0; b < bandCount; b++) {
    const t = b / (bandCount - 1);
    const bandHue = (hue + t * 120 + phase * 0.3) % 360;
    const [br, bg, bb] = hslToRgb(bandHue, 100, 38 + energy * 25);
    const amplitude = (0.05 + energy * 0.22 + mid * 0.12) * H;
    const freq = 1.5 + b * 0.8;
    const phaseOff = phase * (0.022 + b * 0.009);
    const yBase = startY + t * bandH;
    ctx.beginPath();
    ctx.moveTo(0, yBase);
    for (let x = 0; x <= W; x += 3) {
      const xT = x / W;
      const y = yBase
        + Math.sin(xT * Math.PI * 2 * freq + phaseOff) * amplitude * 0.5
        + Math.sin(xT * Math.PI * 2 * (freq * 1.7) + phaseOff * 1.3 + b) * amplitude * 0.25
        + Math.sin(xT * Math.PI * 2 * 0.5 + phaseOff * 0.4) * amplitude * 0.25;
      ctx.lineTo(x, y);
    }
    const alpha = 0.1 + energy * 0.3;
    ctx.strokeStyle = `rgba(${br},${bg},${bb},${alpha + 0.12})`;
    ctx.lineWidth = 1.5 + energy * 1.5;
    ctx.stroke();
    const fillGrad = ctx.createLinearGradient(0, yBase - amplitude, 0, yBase + amplitude);
    fillGrad.addColorStop(0, `rgba(${br},${bg},${bb},0)`);
    fillGrad.addColorStop(0.5, `rgba(${br},${bg},${bb},${alpha})`);
    fillGrad.addColorStop(1, `rgba(${br},${bg},${bb},0)`);
    ctx.lineTo(W, H);
    ctx.lineTo(0, H);
    ctx.closePath();
    ctx.fillStyle = fillGrad;
    ctx.fill();
  }

  const sliceW = W / dataArray.length;
  ctx.beginPath();
  for (let i = 0; i < dataArray.length; i++) {
    const v = dataArray[i] / 128 - 1;
    const x = i * sliceW;
    const y = cy + v * (H * 0.3);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  const [wr, wg, wb] = hslToRgb((hue + 180) % 360, 100, 70);
  ctx.strokeStyle = `rgba(${wr},${wg},${wb},${0.55 + energy * 0.45})`;
  ctx.lineWidth = 2 + energy * 4;
  ctx.shadowColor = `rgb(${wr},${wg},${wb})`;
  ctx.shadowBlur = 10 + energy * 28;
  ctx.stroke();
  ctx.shadowBlur = 0;

  for (const orbit of st.orbits) {
    orbit.angle += orbit.speed + energy * 0.025;
    const r = (orbit.dist + energy * 0.07) * Math.min(W, H);
    const ox = cx + Math.cos(orbit.angle) * r;
    const oy = cy + Math.sin(orbit.angle) * r;
    const [or_, og, ob] = hslToRgb((hue + orbit.dist * 300) % 360, 100, 65);
    const size = 3 + energy * 8 + bass * 10;
    const orbGrad = ctx.createRadialGradient(ox, oy, 0, ox, oy, size);
    orbGrad.addColorStop(0, `rgba(${or_},${og},${ob},0.95)`);
    orbGrad.addColorStop(1, `rgba(${or_},${og},${ob},0)`);
    if (!beginCircle(ctx, ox, oy, size)) continue;
    ctx.fillStyle = orbGrad;
    ctx.fill();
  }
  drawCenterRipples(ctx, W, H, st.ripples, energy);
  const glowR = 55 + energy * 130 + st.glowPulse * 90;
  const centerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
  const [gr, gg, gb] = hslToRgb(hue, 100, 60);
  centerGrad.addColorStop(0, `rgba(${gr},${gg},${gb},${0.35 + energy * 0.55 + st.glowPulse * 0.3})`);
  centerGrad.addColorStop(1, 'rgba(0,0,0,0)');
  if (beginCircle(ctx, cx, cy, glowR)) { ctx.fillStyle = centerGrad; ctx.fill(); }
}

function drawVortex(ctx, W, H, dataArray, shared, st) {
  const { visualEnergy: energy, visualBass: bass, hue, isBeat, beatStrength, phase } = shared;
  const cx = W / 2, cy = H / 2;
  const maxR = Math.max(W, H) * 0.7;
  if (isBeat) {
    st.zoom += 0.3 + bass * 0.6 * Math.min(beatStrength, 2);
    const count = Math.min(5, 2 + Math.floor(beatStrength));
    for (let i = 0; i < count; i++) {
      st.rings.push({ r: 0, speed: 0.016 + st.rand() * 0.014 + bass * 0.025, hue: (hue + st.rand() * 60) % 360, life: 1, width: 1.5 + bass * 5 });
    }
  }
  if (st.rings.length > 28) st.rings = st.rings.slice(-28);
  st.zoom = lerp(st.zoom, 0, 0.06);
  st.twist += 0.004 + energy * 0.025;
  ctx.fillStyle = `rgba(0,0,0,${0.72 - energy * 0.18})`;
  ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 18; i++) {
    const t = (i / 18 + phase * 0.004 + st.zoom * 0.05) % 1;
    const r = t * maxR * 1.1;
    const alpha = (1 - t) * (0.12 + energy * 0.4);
    const [rr, rg, rb] = hslToRgb((hue + i * 20 + phase * 0.5) % 360, 100, 50 + energy * 25);
    ctx.save(); ctx.translate(cx, cy); ctx.rotate(st.twist * (i % 2 === 0 ? 1 : -1) * (1 + i * 0.1));
    const sides = 6 + (i % 3) * 2;
    ctx.beginPath();
    for (let s = 0; s <= sides; s++) {
      const a = (s / sides) * Math.PI * 2;
      const wobble = 1 + 0.05 * Math.sin(a * 3 + phase * 0.05 + i);
      const rx = Math.cos(a) * r * wobble;
      const ry = Math.sin(a) * r * wobble * (H / W);
      s === 0 ? ctx.moveTo(rx, ry) : ctx.lineTo(rx, ry);
    }
    ctx.strokeStyle = `rgba(${rr},${rg},${rb},${alpha})`;
    ctx.lineWidth = 0.5 + energy * 2.5;
    ctx.stroke(); ctx.restore();
  }
  st.rings = st.rings.filter(r => r.r < maxR && r.life > 0);
  for (const ring of st.rings) {
    ring.r += (ring.speed + energy * 0.018) * maxR;
    ring.life = Math.max(0, 1 - ring.r / maxR);
    const [rr, rg, rb] = hslToRgb(ring.hue, 100, 70);
    ctx.save(); ctx.translate(cx, cy);
    if (!beginCircle(ctx, 0, 0, ring.r)) { ctx.restore(); continue; }
    ctx.strokeStyle = `rgba(${rr},${rg},${rb},${ring.life * 0.95})`;
    ctx.lineWidth = ring.width * ring.life; ctx.stroke(); ctx.restore();
  }
  for (const star of st.stars) {
    star.dist += star.speed + energy * 0.016;
    if (star.dist > 0.85) {
      star.dist = 0.01 + st.rand() * 0.05;
      star.angle = st.rand() * Math.PI * 2;
      star.speed = 0.002 + st.rand() * 0.004;
    }
    const r = star.dist * Math.max(W, H) * 0.55;
    const x = cx + Math.cos(star.angle + st.twist * 0.3) * r;
    const y = cy + Math.sin(star.angle + st.twist * 0.3) * r * (H / W);
    const bright = star.dist / 0.85;
    const [sr, sg, sb] = hslToRgb((hue + star.angle * 60) % 360, 80, 70);
    if (beginCircle(ctx, x, y, star.size * bright)) {
      ctx.fillStyle = `rgba(${sr},${sg},${sb},${bright * 0.95})`; ctx.fill();
    }
  }
  const innerR = 70 + energy * 150;
  const innerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, innerR);
  const [hr, hg, hb] = hslToRgb(hue, 100, 60);
  innerGrad.addColorStop(0, `rgba(${hr},${hg},${hb},${0.5 + energy * 0.5})`);
  innerGrad.addColorStop(0.4, `rgba(${hr},${hg},${hb},${0.1 + energy * 0.25})`);
  innerGrad.addColorStop(1, 'rgba(0,0,0,0)');
  if (beginCircle(ctx, cx, cy, innerR)) { ctx.fillStyle = innerGrad; ctx.fill(); }
  drawBottomBars(ctx, W, H, dataArray, hue, 0.13);
}

function drawStrobe(ctx, W, H, dataArray, shared, st) {
  const { visualEnergy: energy, visualBass: bass, hue, colorIdx, isBeat, beatStrength, phase } = shared;
  if (isBeat) {
    st.flash = Math.min(1, 0.7 + beatStrength * 0.15);
    st.beamAngle += Math.PI / 3 + st.rand() * Math.PI / 3;
    st.ripples.push({ x: W / 2, y: H / 2, r: 0, maxR: Math.max(W, H) * 0.7, life: 1, color: COLORS[colorIdx] });
    if (st.ripples.length > 6) st.ripples = st.ripples.slice(-6);
    const [cr, cg, cb] = COLORS[colorIdx];
    for (let i = 0; i < Math.floor(20 + bass * 80 * Math.min(beatStrength, 2)); i++) {
      const a = st.rand() * Math.PI * 2;
      const s = 1 + st.rand() * (3 + bass * 5);
      st.particles.push({ x: W / 2, y: H / 2, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 1, decay: 0.012 + st.rand() * 0.018, size: 1.5 + st.rand() * 3, r: cr, g: cg, b: cb });
    }
  }
  st.flash = Math.max(0, st.flash - 0.07);
  ctx.fillStyle = `rgba(0,0,0,${0.55 + st.flash * 0.3})`;
  ctx.fillRect(0, 0, W, H);
  if (st.flash > 0.05) {
    const [fr, fg, fb] = COLORS[colorIdx];
    ctx.fillStyle = `rgba(${fr},${fg},${fb},${st.flash * 0.28})`;
    ctx.fillRect(0, 0, W, H);
  }
  for (let b = 0; b < 6; b++) {
    const angle = st.beamAngle + (b / 6) * Math.PI * 2 + phase * 0.02;
    const bx = Math.cos(angle), by = Math.sin(angle);
    const intensity = (0.15 + energy * 0.85) * (0.5 + 0.5 * Math.sin(phase + b));
    const color = hslToRgb((hue + b * 45) % 360, 100, 60);
    const grad = ctx.createLinearGradient(W / 2, H / 2, W / 2 + bx * W, H / 2 + by * H);
    grad.addColorStop(0, `rgba(${color[0]},${color[1]},${color[2]},${intensity * 0.9})`);
    grad.addColorStop(1, `rgba(${color[0]},${color[1]},${color[2]},0)`);
    const spread = 0.06 + energy * 0.08;
    const px = -by * spread * W, py = bx * spread * H;
    ctx.beginPath(); ctx.moveTo(W / 2, H / 2); ctx.lineTo(W / 2 + bx * W + px, H / 2 + by * H + py); ctx.lineTo(W / 2 + bx * W - px, H / 2 + by * H - py); ctx.closePath();
    ctx.fillStyle = grad; ctx.fill();
  }
  st.ripples = st.ripples.filter(rp => rp.life > 0);
  for (const rp of st.ripples) {
    rp.r = lerp(rp.r, rp.maxR, 0.09); rp.life -= 0.022;
    const [rr, rg, rb] = rp.color;
    if (beginCircle(ctx, rp.x, rp.y, rp.r)) {
      const life = Math.max(0, rp.life);
      ctx.strokeStyle = `rgba(${rr},${rg},${rb},${life * 0.75})`; ctx.lineWidth = 2 + life * 4; ctx.stroke();
    }
  }
  st.particles = st.particles.filter(p => p.life > 0).slice(-900);
  for (const p of st.particles) {
    p.x += p.vx; p.y += p.vy; p.vy += 0.05; p.vx *= 0.98; p.life -= p.decay;
    const radius = p.size * Math.max(0, p.life);
    if (radius <= 0) continue;
    if (beginCircle(ctx, p.x, p.y, radius)) {
      ctx.fillStyle = `rgba(${p.r},${p.g},${p.b},${Math.max(0, p.life)})`; ctx.fill();
    }
  }
  for (let i = 0; i < 64; i++) {
    const raw = (dataArray[Math.floor(i * dataArray.length / 64)] || 0) / 255;
    st.bars[i] = raw > st.bars[i] ? raw : st.bars[i] * 0.82 + raw * 0.18;
    const bh = st.bars[i] * H * 0.24;
    const [br, bg, bb] = hslToRgb((hue + i * 3) % 360, 100, 55);
    ctx.fillStyle = `rgba(${br},${bg},${bb},${0.5 + st.bars[i] * 0.5})`;
    const barW = W / 64;
    ctx.fillRect(i * barW, H - bh, barW - 1, bh);
    ctx.fillRect(i * barW, 0, barW - 1, bh * 0.35);
  }
}

function drawFlash(ctx, W, H, _data, shared, st) {
  const { isBeat, beatStrength, hue } = shared;
  if (isBeat) {
    st.flash = Math.min(1, 0.85 + beatStrength * 0.1);
    st.color = st.rand() < 0.3 ? hslToRgb(hue, 100, 72) : [255, 255, 255];
  }
  st.flash *= 0.7;
  ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H);
  if (st.flash > 0.008) {
    const [r, g, b] = st.color;
    ctx.fillStyle = `rgba(${r},${g},${b},${st.flash})`;
    ctx.fillRect(0, 0, W, H);
  }
}

function drawJourney(ctx, W, H, shared, st, perspective) {
  const { visualEnergy: en, visualBass: bass, hue, isBeat, beatStrength } = shared;
  st.frameCount++;
  st.scrollX += 0.8 + en * 2 + bass;
  if (isBeat) {
    st.beatFlash = Math.min(1, 0.4 + beatStrength * 0.2);
    st.winPulse = 1; st.wavePulse = 1; st.birdFlap += Math.PI * 0.75;
    if (!st.looping && beatStrength > 0.62) {
      st.looping = true; st.loopAngle = 0; st.loopDir = st.rand() < 0.5 ? 1 : -1;
    }
    st.beatRings.push({ r: 20, maxR: Math.max(W, H) * 0.72, life: 1, hue });
    if (st.beatRings.length > 8) st.beatRings = st.beatRings.slice(-8);
  }
  st.beatFlash = Math.max(0, st.beatFlash - 0.03);
  st.winPulse = Math.max(0, st.winPulse - 0.022);
  st.wavePulse = Math.max(0, st.wavePulse - 0.028);
  st.birdFlap += 0.055 + en * 0.11;
  const scene = Math.floor(st.frameCount / 1800) % 4;
  if (scene === 0) drawCity(ctx, W, H, st, en);
  else if (scene === 1) drawMountains(ctx, W, H, st);
  else if (scene === 2) drawCoast(ctx, W, H, st, en);
  else drawClouds(ctx, W, H, st);

  const baseX = perspective ? W * 0.5 : W * 0.28;
  const baseY = H * 0.48 - en * H * 0.3;
  let bx = baseX, by = baseY, rot = 0;
  if (st.looping) {
    st.loopAngle += 0.1 * st.loopDir;
    bx = baseX + Math.sin(st.loopAngle) * 24;
    by = baseY - Math.sin(st.loopAngle) * 58;
    rot = st.loopAngle * st.loopDir;
    if (Math.abs(st.loopAngle) >= Math.PI * 2) { st.looping = false; st.loopAngle = 0; }
  }
  st.trail.unshift({ x: bx, y: by });
  if (st.trail.length > 14) st.trail.pop();
  st.beatRings = st.beatRings.filter(r => r.life > 0);
  for (const ring of st.beatRings) {
    ring.r += (ring.maxR - ring.r) * 0.09; ring.life -= 0.028;
    const [rr, rg, rb] = hslToRgb(ring.hue, 100, 72);
    if (beginCircle(ctx, bx, by, ring.r)) {
      const life = Math.max(0, ring.life);
      ctx.strokeStyle = `rgba(${rr},${rg},${rb},${life * 0.5})`; ctx.lineWidth = 2 + life * 4; ctx.stroke();
    }
  }
  for (let i = st.trail.length - 1; i > 0; i--) {
    const p = st.trail[i];
    const a = (1 - i / st.trail.length) * 0.42;
    const r = (1 - i / st.trail.length) * (16 + en * 22);
    ctx.fillStyle = `hsla(${(hue + i * 7) % 360},100%,65%,${a})`;
    if (beginCircle(ctx, p.x, p.y, r)) ctx.fill();
  }
  drawNeonBird(ctx, bx, by, st.birdFlap, en, hue, rot, perspective);
  if (st.beatFlash > 0.005) {
    const [fr, fg, fb] = hslToRgb(hue, 80, 65);
    ctx.fillStyle = `rgba(${fr},${fg},${fb},${st.beatFlash * 0.15})`;
    ctx.fillRect(0, 0, W, H);
  }
}

function drawCity(ctx, W, H, st) {
  const WORLD = W * 6;
  const sg = ctx.createLinearGradient(0, 0, 0, H * 0.68);
  sg.addColorStop(0, '#020010'); sg.addColorStop(1, '#0f0028');
  ctx.fillStyle = sg; ctx.fillRect(0, 0, W, H);
  for (const s of st.stars) {
    const sx = ((s.nx * WORLD - st.scrollX * 0.02) % WORLD + WORLD) % WORLD - 10;
    if (sx < -5 || sx > W + 5) continue;
    ctx.fillStyle = `rgba(255,255,255,${0.28 + Math.sin(st.frameCount * 0.025 + s.nx * 80) * 0.12})`;
    if (beginCircle(ctx, sx, s.ny * H, s.r)) ctx.fill();
  }
  for (const b of st.bldgs) {
    const bw = b.w * 1.35;
    const bx = ((b.nx * WORLD - st.scrollX * 0.52) % WORLD + WORLD) % WORLD - bw;
    if (bx > W + bw || bx < -bw) continue;
    const bh = b.h * H * 0.52 + H * 0.04;
    const top = H * 0.68 - bh;
    ctx.fillStyle = '#04000e'; ctx.fillRect(bx, top, bw, bh + H * 0.32);
    if (b.mast) ctx.fillRect(bx + bw / 2 - 1, top - 22, 2, 22);
    const wa = 0.18 + st.winPulse * 0.68;
    for (let wy = top + 8; wy < top + bh - 10; wy += 14) {
      for (let wx = bx + 5; wx < bx + bw - 7; wx += 10) {
        if (Math.sin(wx * 2.3 + b.wseed) + Math.cos(wy * 3.1 + b.wseed * 0.7) > 0.5) {
          ctx.fillStyle = `rgba(255,215,80,${wa})`; ctx.fillRect(wx, wy, 5, 7);
        }
      }
    }
  }
  ctx.fillStyle = '#010005'; ctx.fillRect(0, H * 0.68, W, H * 0.32);
  ctx.strokeStyle = 'rgba(255,210,40,0.22)'; ctx.lineWidth = 2; ctx.setLineDash([28, 18]);
  ctx.beginPath(); ctx.moveTo(0, H * 0.8); ctx.lineTo(W, H * 0.8); ctx.stroke(); ctx.setLineDash([]);
}

function drawMountains(ctx, W, H, st) {
  const sg = ctx.createLinearGradient(0, 0, 0, H * 0.7);
  sg.addColorStop(0, '#08001c'); sg.addColorStop(0.28, '#2a0050'); sg.addColorStop(0.62, '#aa2400'); sg.addColorStop(1, '#ff5800');
  ctx.fillStyle = sg; ctx.fillRect(0, 0, W, H);
  const sunG = ctx.createRadialGradient(W * 0.64, H * 0.63, 0, W * 0.64, H * 0.63, 190);
  sunG.addColorStop(0, 'rgba(255,210,50,0.85)'); sunG.addColorStop(0.18, 'rgba(255,120,0,0.45)'); sunG.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = sunG; ctx.fillRect(0, 0, W, H);
  const layers = [{ spd: 0.07, yb: 0.5, col: '#1c0038' }, { spd: 0.17, yb: 0.58, col: '#0e001e' }, { spd: 0.36, yb: 0.67, col: '#060010' }];
  for (const lyr of layers) {
    const off = st.scrollX * lyr.spd;
    ctx.fillStyle = lyr.col; ctx.beginPath(); ctx.moveTo(0, H);
    for (let x = 0; x <= W + 2; x += 2) {
      const nx = (x + off) / W;
      const y = lyr.yb - Math.abs(Math.sin(nx * 7.2 + lyr.spd * 4)) * 0.18 - Math.abs(Math.sin(nx * 3.8 + 1.5)) * 0.12;
      ctx.lineTo(x, y * H);
    }
    ctx.lineTo(W, H); ctx.closePath(); ctx.fill();
  }
}

function drawCoast(ctx, W, H, st, energy) {
  const sg = ctx.createLinearGradient(0, 0, 0, H * 0.52);
  sg.addColorStop(0, '#00101e'); sg.addColorStop(1, '#0050a0');
  ctx.fillStyle = sg; ctx.fillRect(0, 0, W, H);
  const seaG = ctx.createLinearGradient(0, H * 0.52, 0, H);
  seaG.addColorStop(0, '#004888'); seaG.addColorStop(1, '#001428');
  ctx.fillStyle = seaG; ctx.fillRect(0, H * 0.52, W, H * 0.48);
  for (const w of st.waves) {
    w.phase += w.spd * (1 + energy * 0.6);
    const wy = w.ny * H;
    ctx.beginPath(); ctx.moveTo(0, wy);
    for (let x = 0; x <= W; x += 4) ctx.lineTo(x, wy + Math.sin(x * 0.014 + w.phase + st.scrollX * 0.0025) * w.amp);
    ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath();
    ctx.fillStyle = `rgba(0,100,180,${0.05 + (w.ny - 0.62) * 0.18})`; ctx.fill();
    if (st.wavePulse > 0.08) { ctx.strokeStyle = `rgba(255,255,255,${st.wavePulse * 0.35})`; ctx.stroke(); }
  }
}

function drawClouds(ctx, W, H, st) {
  const sg = ctx.createLinearGradient(0, 0, 0, H);
  sg.addColorStop(0, '#00082a'); sg.addColorStop(0.35, '#0055b0'); sg.addColorStop(0.7, '#55a8ff'); sg.addColorStop(1, '#b8dcff');
  ctx.fillStyle = sg; ctx.fillRect(0, 0, W, H);
  const sunG = ctx.createRadialGradient(W * 0.74, H * 0.18, 0, W * 0.74, H * 0.18, 130);
  sunG.addColorStop(0, 'rgba(255,255,210,0.92)'); sunG.addColorStop(0.5, 'rgba(255,220,80,0.18)'); sunG.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = sunG; ctx.fillRect(0, 0, W, H);
  const WORLD = W * 5;
  for (const c of st.clouds) {
    const cx = ((c.nx * WORLD - st.scrollX * (0.08 + c.lyr * 0.07)) % WORLD + WORLD) % WORLD - c.w;
    if (cx > W + c.w || cx < -c.w * 2) continue;
    const cy = H * 0.56 + c.lyr * 28 + c.ny * H * 0.22;
    ctx.fillStyle = `rgba(255,255,255,${0.6 + c.lyr * 0.12})`;
    cloudPuff(ctx, cx, cy, c.w, c.h);
  }
}

function cloudPuff(ctx, x, y, w, h) {
  const r = h / 2;
  if (beginCircle(ctx, x + w * 0.22, y, r * 0.88)) ctx.fill();
  if (beginCircle(ctx, x + w * 0.5, y - h * 0.2, r * 1.08)) ctx.fill();
  if (beginCircle(ctx, x + w * 0.78, y, r * 0.92)) ctx.fill();
  ctx.fillRect(x + r * 0.9, y - r, w - r * 1.8, r * 1.2);
}

function drawNeonBird(ctx, cx, cy, flapAngle, en, hue, rot, bigger) {
  const wf = Math.sin(flapAngle);
  const sc = (bigger ? 1.3 : 1.2) + en * 0.18;
  const color = `hsl(${hue}, 100%, 62%)`;
  const glow = `hsl(${(hue + 40) % 360}, 100%, 80%)`;
  ctx.save(); ctx.translate(cx, cy); ctx.rotate(rot); ctx.scale(sc, sc);
  ctx.shadowColor = glow; ctx.shadowBlur = 15 + en * 18; ctx.fillStyle = color;
  ctx.beginPath(); ctx.ellipse(0, 0, bigger ? 10 : 18, bigger ? 18 : 7, 0.15, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(18, -3, 8, 6, -0.2, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.moveTo(24, -4); ctx.lineTo(34, -2); ctx.lineTo(24, -1); ctx.fill();
  ctx.beginPath(); ctx.moveTo(2, -4); ctx.bezierCurveTo(-8, wf * 28 - 20, -28, wf * 30 - 10, -38, wf * 30); ctx.bezierCurveTo(-28, wf * 30 + 8, -8, 2, 2, 4); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(2, 4); ctx.bezierCurveTo(-8, wf * 14 + 8, -22, wf * 16 + 12, -30, wf * 16 + 16); ctx.bezierCurveTo(-22, wf * 16 + 20, -8, 10, 2, 8); ctx.closePath(); ctx.fill();
  ctx.strokeStyle = glow; ctx.lineWidth = 1.2; ctx.globalAlpha = 0.4;
  ctx.beginPath(); ctx.moveTo(2, -4); ctx.bezierCurveTo(-8, wf * 28 - 20, -28, wf * 30 - 10, -38, wf * 30); ctx.stroke();
  ctx.restore();
}

function drawCenterRipples(ctx, W, H, ripples, energy) {
  const cx = W / 2, cy = H / 2;
  for (const rp of ripples.filter(rp => rp.life > 0)) {
    rp.r += 5 + energy * 14; rp.life -= 0.018;
    const [rr, rg, rb] = hslToRgb(rp.hue, 100, 65);
    if (beginCircle(ctx, cx, cy, rp.r)) {
      const life = Math.max(0, rp.life);
      ctx.strokeStyle = `rgba(${rr},${rg},${rb},${life * 0.55})`; ctx.lineWidth = 2.5 + life * 4; ctx.stroke();
    }
  }
}

function drawBottomBars(ctx, W, H, dataArray, hue, heightScale) {
  const barW = W / 64;
  for (let i = 0; i < 64; i++) {
    const raw = (dataArray[Math.floor(i * dataArray.length / 64)] || 0) / 255;
    const bh = raw * H * heightScale;
    if (bh < 1) continue;
    const [br, bg, bb] = hslToRgb((hue + i * 4) % 360, 100, 55);
    ctx.fillStyle = `rgba(${br},${bg},${bb},0.55)`;
    ctx.fillRect(i * barW, H - bh, barW - 1, bh);
  }
}
